// sync-retry.js — the cloud push tries again on its own.
//
// Until this existed, exactly three things could move a doc out of the dirty
// map: the user saving again (`Sync.schedulePush`), a `restoreFromCloud`, and
// the unlock hook in js/vault-ui.js. Nothing listened for `online`, and nothing
// ever re-attempted a push that had simply failed. So a laptop that lost wifi
// for a minute mid-save kept its changes local until the agent happened to
// touch that record again, which for a quote they had finished could be never.
// js/sync-status-banner.js told them writes were blocked; nothing retried.
//
// Its own file rather than js/supabase-sync.js because that file is 1,000 lines
// past the 1,800-line budget with no grandfather ceiling, and rather than
// js/sync-facade.js because a timer-owning module should be readable on its own.
//
// NO SIDE EFFECTS AT EVAL. tests/load-html.cjs inlines every eager `<script
// src>` into every JSDOM suite, so a listener or an interval registered at
// eval time leaks a handle in hundreds of them. init() is called once from
// js/app-boot.js; stop() exists so tests do not leak a handle. Same shape as
// js/sync-status-banner.js.

window.SyncRetry = (() => {
    'use strict';

    // 60 s, doubling to a 10-minute ceiling. Slow on purpose: the work is
    // already safe on this device, so the only thing a fast loop buys is load
    // on a server that has just said no.
    const BASE_MS = 60_000;
    const MAX_MS = 600_000;

    let _timer = null;
    let _delayMs = BASE_MS;
    let _lastAttemptAt = 0;
    let _lastOutcome = 'idle';   // idle | ok | failed | skipped | gated
    let _running = false;

    function _sync() {
        return (typeof window !== 'undefined') ? window.Sync : null;
    }

    function _pendingCount() {
        const s = _sync();
        if (!s || typeof s.getDirtyDocs !== 'function') return 0;
        try { const d = s.getDirtyDocs(); return Array.isArray(d) ? d.length : 0; } catch (_) { return 0; }
    }

    /**
     * Why a push must NOT be attempted right now, or '' to go ahead.
     *
     * Checked BEFORE the call, never relied on inside it. A locked-vault
     * flushPendingPush is not free: js/supabase-sync.js answers 'crypto-locked'
     * but first runs _deferPushUntilUnlock() -> _logCryptoPaused(), which writes
     * an ok:false ActivityLog row once every five minutes. A 60-second loop
     * would therefore paint the header pill permanently red on a vault the
     * agent had deliberately left locked, which is the definition of a warning
     * people learn to ignore.
     *
     * The device-only, MFA and signed-in halves come off the facade's own
     * getters, so this loop and every other write path read one gate.
     */
    function _gated() {
        const s = _sync();
        if (!s) return 'unavailable';
        try { if (s.syncDisabled || s.mfaBlocked || s.policyBlocked) return 'gated'; } catch (_) { return 'gated'; }
        const ch = (typeof window !== 'undefined') ? window.CryptoHelper : null;
        try {
            if (ch && typeof ch.isV2Enabled === 'function' && ch.isV2Enabled()
                && typeof ch.isV2Unlocked === 'function' && !ch.isV2Unlocked()) return 'locked';
        } catch (_) { /* treat an unreadable vault as unlocked; the push itself still guards */ }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
        return '';
    }

    function _arm(ms) {
        clearTimeout(_timer);
        _timer = setTimeout(_tick, ms);
    }

    /**
     * Stand down at the base interval WITHOUT growing the backoff.
     *
     * A gated or idle tick is not a failure, and punishing it would mean an
     * agent who left their vault locked over lunch came back to a ten-minute
     * retry interval on a sync that was never broken.
     */
    function _sleep(outcome) {
        _delayMs = BASE_MS;
        if (outcome) _lastOutcome = outcome;
        _arm(BASE_MS);
    }

    async function _tick() {
        _timer = null;
        if (_running) return;
        if (!_pendingCount()) { _sleep('idle'); return; }

        const why = _gated();
        if (why) { _sleep(why === 'gated' || why === 'unavailable' ? 'gated' : why); return; }

        _running = true;
        _lastAttemptAt = Date.now();
        let res;
        try { res = await _sync().flushPendingPush(); } catch (_) { res = { ok: false }; }
        _running = false;

        // `skipped` covers push-in-progress, restore-in-progress,
        // recovery-in-progress and no-pending-push — ordinary states that
        // reschedule themselves. Doubling the backoff on those punishes a
        // healthy sync for being busy.
        if (res && res.skipped) { _sleep('skipped'); return; }
        if (res && res.ok) { _lastOutcome = 'ok'; _sleep(); return; }

        _lastOutcome = 'failed';
        _delayMs = Math.min(_delayMs * 2, MAX_MS);
        _arm(_delayMs);
    }

    /** Wake up now and reset the backoff. The reason to retry just changed. */
    function _wake() {
        _delayMs = BASE_MS;
        _arm(0);
    }

    function retryNow() { _wake(); }

    function status() {
        return {
            pending: _pendingCount(),
            lastAttemptAt: _lastAttemptAt,
            lastOutcome: _lastOutcome,
            nextAttemptAt: _timer ? Date.now() + _delayMs : 0,
            gatedBy: _gated(),
        };
    }

    function init() {
        if (typeof window === 'undefined') return;
        // init() can run twice (js/app-boot.js re-entry); listeners are removed
        // first so a second call does not double them up.
        window.removeEventListener('online', _wake);
        window.addEventListener('online', _wake);
        // The unlock event is the point of (b): a locked vault goes DORMANT
        // rather than backing off, so something has to wake it. VaultUI already
        // flushes on unlock, which means this listener is a backstop, not the
        // primary path.
        window.removeEventListener('vault:unlocked', _wake);
        window.addEventListener('vault:unlocked', _wake);
        _arm(BASE_MS);
    }

    /** Tear down the timer + listeners. Exists so tests don't leak a handle. */
    function stop() {
        clearTimeout(_timer);
        _timer = null;
        _running = false;
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', _wake);
            window.removeEventListener('vault:unlocked', _wake);
        }
    }

    return {
        init, stop, status, retryNow,
        _BASE_MS: BASE_MS,
        _MAX_MS: MAX_MS,
        // Tests only: run one tick without waiting out the interval.
        _tickNow: () => _tick(),
    };
})();
